'use client';

import { useState } from 'react';
import { Button } from '../button/button';
import { Flex } from '../flex/flex';
import { Text } from '../text/text';
import { Stack } from '../stack/stack';
import { ImageCrop, aspectRatioToDecimal } from '../image-crop/image-crop';
import { FileUpload } from '../file-upload/file-upload';
import { DropzoneArea } from '../dropzone-area/dropzone-area';
import { Pencil } from 'lucide-react';
import { themeVars, transparencyBackground } from '@instamenu/ui-vanilla-extract';
import { clsx } from 'clsx';
import Image from 'next/image';
import { Box } from '../box/box';

export type ImageUploadProps = {
  title: string;
  alt: string;
  currentImageUrl?: string | null;
  aspect: string;
  imageWidth: number;
  cropPadding?: number;
  showAspectRatioSelector?: boolean;
  onChange: (dataUri: string) => Promise<void>;
  containerClassName?: string;
  imageClassName?: string;
};

export const ImageUpload = ({
  title,
  alt,
  currentImageUrl,
  aspect,
  imageWidth,
  cropPadding,
  showAspectRatioSelector = false,
  onChange,
  containerClassName,
  imageClassName,
}: ImageUploadProps) => {
  const [imageSrc, setImageSrc] = useState<string | null | undefined>();

  return (
    <Stack className={containerClassName} css={{ gap: themeVars.pad[24] }}>
      <DropzoneArea setFile={setImageSrc}>
        <Stack css={{ gap: themeVars.pad[12] }}>
          <Flex css={{ gap: themeVars.pad[12], alignItems: 'center' }}>
            <Text variant="h2">{title}</Text>
            <FileUpload onFileSelect={setImageSrc} />
          </Flex>
          {!imageSrc && currentImageUrl && (
            <Box css={{ maxWidth: imageWidth, position: 'relative' }}>
              <Button
                size="xs"
                onClick={() => setImageSrc(currentImageUrl)}
                variant="secondary"
                css={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
              >
                <Pencil size={16} />
              </Button>
              <Image
                src={currentImageUrl}
                alt={alt}
                width={imageWidth}
                height={imageWidth / aspectRatioToDecimal(aspect)}
                priority
                className={clsx(imageClassName, transparencyBackground)}
              />
            </Box>
          )}
          {imageSrc && (
            <ImageCrop
              imageSrc={imageSrc}
              setImageSrc={setImageSrc}
              aspect={aspect}
              onCrop={onChange}
              padding={cropPadding}
              showAspectRatioSelector={showAspectRatioSelector}
            />
          )}
        </Stack>
      </DropzoneArea>
    </Stack>
  );
};
